import { cn } from '@/lib/cn';
import { Scale, TrendingUp, Users, Activity } from 'lucide-react';

interface SamplingResultsCardProps {
  abw: number | null;
  biomass: number | null;
  survivalRate: number | null;
  previousAbw?: number | null;
  className?: string;
}

export function SamplingResultsCard({
  abw,
  biomass,
  survivalRate,
  previousAbw,
  className,
}: SamplingResultsCardProps) {
  const hasData = abw !== null && abw > 0;
  const abwGrowth = previousAbw && abw ? ((abw - previousAbw) / previousAbw) * 100 : null;

  if (!hasData) {
    return (
      <div className={cn('bg-gray-50 rounded-xl border border-gray-200 p-5', className)}>
        <div className="flex items-center gap-3 text-gray-500">
          <Scale className="w-5 h-5" />
          <p className="text-sm">Enter sample data to see calculations</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'relative overflow-hidden rounded-xl border-2',
      'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50',
      'border-indigo-200',
      className
    )}>
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="sampling-pattern" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="16" cy="16" r="8" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#sampling-pattern)" />
        </svg>
      </div>

      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-indigo-100">
            <Activity className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-indigo-900">Sampling Results</h3>
        </div>

        {/* Results grid */}
        <div className="grid grid-cols-3 gap-4">
          {/* ABW */}
          <div className="bg-white/60 rounded-lg p-4 border border-indigo-100">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="w-4 h-4 text-indigo-500" />
              <p className="text-xs font-medium text-indigo-600">ABW</p>
            </div>
            <p className="text-2xl font-bold text-indigo-900 tabular-nums">
              {abw?.toFixed(2)}
              <span className="text-sm font-normal text-indigo-400 ml-1">g</span>
            </p>
            {abwGrowth !== null && (
              <p className={cn(
                'text-xs mt-1 flex items-center gap-1',
                abwGrowth > 0 ? 'text-emerald-600' : 'text-red-600'
              )}>
                <TrendingUp className={cn('w-3 h-3', abwGrowth < 0 && 'rotate-180')} />
                {abwGrowth > 0 ? '+' : ''}{abwGrowth.toFixed(1)}% vs last
              </p>
            )}
          </div>

          {/* Biomass */}
          <div className="bg-white/60 rounded-lg p-4 border border-indigo-100">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <p className="text-xs font-medium text-indigo-600">Biomass</p>
            </div>
            <p className="text-2xl font-bold text-indigo-900 tabular-nums">
              {biomass !== null ? biomass.toFixed(0) : '—'}
              <span className="text-sm font-normal text-indigo-400 ml-1">kg</span>
            </p>
          </div>

          {/* Survival */}
          <div className="bg-white/60 rounded-lg p-4 border border-indigo-100">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-indigo-500" />
              <p className="text-xs font-medium text-indigo-600">Survival</p>
            </div>
            <p className={cn(
              'text-2xl font-bold tabular-nums',
              survivalRate !== null && survivalRate >= 80
                ? 'text-emerald-600'
                : survivalRate !== null && survivalRate >= 60
                  ? 'text-amber-600'
                  : 'text-indigo-900'
            )}>
              {survivalRate !== null ? survivalRate.toFixed(1) : '—'}
              <span className="text-sm font-normal text-indigo-400 ml-1">%</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
